import "./style.scss";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../BtnDefault/BtnDefault";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { typeOfDocumentQRCode, whoseQRCode } from "@/src/constants/documentsQRCode";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { reloadPageAfterNewDocumentConfirm } from "@/src/utils/admissionOffice/confirmNewDocumentReload";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import DatabaseSvg from "@/src/svg/admissionOfficePage/DatabaseSvg";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import FormEmail from "../FormEmail/FormEmail";

interface IAOPConfig {
  setDocumentsPDFPath: React.Dispatch<React.SetStateAction<string>>;
}

interface IAOPFormValues {
  fullname: string;
  whose_doc: string;
  date: string;
  type: string;
  file: File | null;
}

const today = new Date();

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<IAOPConfig> = ({ setDocumentsPDFPath }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      file: null,
      date: formatDateForInput(today),
    },
  });
  const dispatch = useDispatch();

  const [isGenerated, setIsGenerated] = useState<boolean>(false);
  const [isEmailBool, setIsEmailBool] = useState<boolean>(false);
  const [isName, setIsName] = useState<string>("");
  const [isSeria, setIsSeria] = useState<string>("");
  const whoseQRCodeAuto = useAutocomplete(whoseQRCode);
  const typeOfDocumentQRCodeAuto = useAutocomplete(typeOfDocumentQRCode.map((i) => i.label));

  const isFileTypeValid = (file: File) => {
    const currentType = getValues("type");

    if (currentType === "basic-word") {
      return file.name.endsWith(".docx");
    }

    if (currentType === "basic-pdf") {
      return file.name.endsWith(".pdf");
    }

    return true;
  };

  const generateNewDocument = async () => {
    const values = getValues();
    const payload = {
      fullname: values.fullname,
      whose_doc: values.whose_doc,
      userOffice: "01",
      date: parseDate(values.date),
      type: values.type,
      file: values.file,
    };

    dispatch(setIsPreloyder(true));
    try {
      let response = await admissionOfficeRequests.createDocumentsQRCode(payload);
      if (response && typeof response.pdf_path === "string") {
        setIsGenerated(true);
        setDocumentsPDFPath(response.pdf_path);
        setIsName(values.fullname);
        setIsSeria(response.seria);
        console.log(response, "resonse");
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">Documents QR Code</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseQRCodeAuto.query}
                onChange={whoseQRCodeAuto.onChange}
                onSelect={(v) => {
                  whoseQRCodeAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseQRCodeAuto.isOpen}
                options={whoseQRCodeAuto.filtered}
                error={!!errors.whose_doc}
                required
                inputProps={{
                  register: register("whose_doc", { required: true }),
                  close: whoseQRCodeAuto.close,
                  setOpen: whoseQRCodeAuto.onClick,
                  onArrowClick: whoseQRCodeAuto.onArrowClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input date">
              <div className="form-group-fields-input-label">
                <label htmlFor="date">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="date" id="date" type="date" {...register("date", { required: true })} />
              {errors.date && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <AutocompleteInput
                label="Type of document"
                value={typeOfDocumentQRCodeAuto.query}
                onChange={typeOfDocumentQRCodeAuto.onChange}
                onSelect={(label) => {
                  const selected = typeOfDocumentQRCode.find((i) => i.label === label);

                  if (!selected) return;

                  typeOfDocumentQRCodeAuto.onSelect(label);

                  setValue("type", selected.value);
                }}
                isOpen={typeOfDocumentQRCodeAuto.isOpen}
                options={typeOfDocumentQRCodeAuto.filtered}
                error={!!errors.type}
                required
                inputProps={{
                  register: register("type", { required: true }),
                  close: typeOfDocumentQRCodeAuto.close,
                  setOpen: typeOfDocumentQRCodeAuto.onClick,
                  onArrowClick: typeOfDocumentQRCodeAuto.onArrowClick,
                }}
                identifier={"type-of-document"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullname", { required: true })} />
              {errors.fullname && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullname")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-file">
            <div className="form-group-file-title">
              <p>File</p>
            </div>
            <div
              className="form-group-file-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (!file) return;

                if (!isFileTypeValid(file)) {
                  alert("Invalid file type for selected document type");
                  return;
                }

                setValue("file", file, { shouldValidate: true });
                clearErrors("file");
              }}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  if (!isFileTypeValid(file)) {
                    alert("Invalid file type for selected document type");
                    e.target.value = "";
                    return;
                  }

                  setValue("file", file, { shouldValidate: true });
                  clearErrors("file");
                }}
              />

              <div className="form-group-file-dropzone-placeholder">
                <p>Drag & Drop Files Here</p>
                <p>or</p>
                <span>Browse Files</span>
              </div>
            </div>
            {watch("file")?.name && (
              <div className="form-group-file-name">
                <p>{watch("file")?.name}</p>
              </div>
            )}
          </div>
          <div className="form-group-buttons">
            <div className="form-group-buttons-pack">
              <BtnDefault onClick={handleSubmit(generateNewDocument)}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
              <BtnDefault disabled={!isGenerated}>
                <p>Download PDF</p>
                <AODSvgExport.DownloadSvg />
              </BtnDefault>
              <Link to={"/admissions-office-database"}>
                <BtnDefault reverse={true}>
                  <p>Database</p>
                  <DatabaseSvg />
                </BtnDefault>
              </Link>
              <BtnDefault disabled={!isGenerated} onClick={() => setIsEmailBool(true)}>
                <p>Send Email</p>
              </BtnDefault>
            </div>
            <div className="form-group-buttons-refresh" onClick={reloadPageAfterNewDocumentConfirm}>
              <BtnDefault onlyIcon={true}>
                <AODSvgExport.RefreshSvg />
              </BtnDefault>
            </div>
          </div>
        </form>
        {isEmailBool && <FormEmail isName={isName} isSeria={isSeria} />}
      </div>
    </>
  );
};

export default AOPConfig;
